import React, { useMemo, useState, ReactNode, useContext } from 'react';
import { createContext } from 'react';

export interface UserCreditDetailsType {
  firstName: string;
  lastName: string;
  consentStatus: number;
  phone: string;
  expiryAt: string;
  lastConsentAt: string;
  nextRefreshAt: string;
  dob: string;
  email: string;
  pan: string;
  pincode: string;
  address: string;
  city: string;
  leads: any;
}

export interface CreditDetailsType {
  name: string;
  creditScore: number;
  lastConsentAt: string;
  nextRefreshAt: string;
  expiryAt: string;
  consentStatus: number;
  creditAge: number;
  creditCardUtilisation: number;
  creditEnquiries: number;
  paymentHistory: number;
  totalAccounts: number;
  responseJson: any;
}

// Define the shape of the context value
interface CreditContextType {
  userCreditDetails: UserCreditDetailsType;
  setUserCreditDetails: React.Dispatch<
    React.SetStateAction<UserCreditDetailsType>
  >;
  creditDetails: CreditDetailsType;
  setCreditDetails: React.Dispatch<React.SetStateAction<CreditDetailsType>>;
}

export const defaultUserCreditDetails: UserCreditDetailsType = {
  firstName: '',
  lastName: '',
  consentStatus: 0,
  phone: '',
  expiryAt: '',
  lastConsentAt: '',
  nextRefreshAt: '',
  dob: '',
  email: '',
  pan: '',
  pincode: '',
  address: '',
  city: '',
  leads: [],
};

export const defaultCreditDetails: CreditDetailsType = {
  name: '',
  creditScore: 0,
  lastConsentAt: '',
  nextRefreshAt: '',
  expiryAt: '',
  consentStatus: 0,
  creditAge: 0,
  creditCardUtilisation: 0,
  creditEnquiries: 0,
  paymentHistory: 0,
  totalAccounts: 0,
  responseJson: {},
};

export const CreditContext = createContext<CreditContextType | undefined>(
  undefined
);

interface CreditScoreContextProps {
  children: ReactNode;
}

export default function CreditScoreContext({
  children,
}: CreditScoreContextProps) {
  const [userCreditDetails, setUserCreditDetails] =
    useState<UserCreditDetailsType>(defaultUserCreditDetails);
  const [creditDetails, setCreditDetails] =
    useState<CreditDetailsType>(defaultCreditDetails);

  const value = useMemo(
    () => ({
      userCreditDetails,
      setUserCreditDetails,
      creditDetails,
      setCreditDetails,
    }),
    [userCreditDetails, creditDetails]
  );

  // console.log("CreditScoreContext value =======> ", value);
  return (
    <CreditContext.Provider value={value}>{children}</CreditContext.Provider>
  );
}

// Custom hook for using the CreditContext
export function useCreditContext() {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error(
      'useCreditContext must be used within a CreditScoreProvider'
    );
  }
  return context;
}
